<template>
	<div class="g-block-pay-audio-playlist" :class="{
		'g-block-pay-audio-playlist--edit': editable,
		'is-active': editable && active,
		'g-block-pay-audio-playlist--h5': h5,
		'g-block-pay-audio-playlist--compact': compact,
		'g-block-pay-audio-playlist--unlocked': isUnlocked,
		'is-playing': isPlaying,
	}" @click="onWrapClick">
		<div class="g-block-pay-audio-playlist__card">
			<div class="g-block-pay-audio-playlist__preview">
				<div class="g-block-pay-audio-playlist__preview-cover">
					<img v-if="activeCoverUrl" :src="activeCoverUrl" alt="" />
					<i v-else class="mbm-iconfont mbm-yinpin"></i>
				</div>
				<div class="g-block-pay-audio-playlist__preview-main">
					<div class="g-block-pay-audio-playlist__listening">
						<span class="g-block-pay-audio-playlist__dot"></span>
						<span>{{ isUnlocked ? (isPlaying ? '正在播放' : '点击播放') : (isPlaying ? '正在试听' : '点击试听') }}</span>
					</div>
					<div class="g-block-pay-audio-playlist__preview-title">{{ activeTitle }}</div>
					<div class="g-block-pay-audio-playlist__preview-sub">{{ artistMetaText }}</div>
					<div class="g-block-pay-audio-playlist__progress">
						<div ref="progressTrack" class="g-block-pay-audio-playlist__progress-track" role="slider"
							:aria-valuemin="0" :aria-valuemax="Math.floor(duration || 0)"
							:aria-valuenow="Math.floor(isDragging ? draggingTime : currentTime)" aria-label="播放进度"
							:class="{ 'is-interactive': duration > 0 }" @click.stop="onProgressClick"
							@mousedown.stop="onProgressMouseDown" @touchstart.stop.prevent="onProgressTouchStart">
							<div class="g-block-pay-audio-playlist__progress-fill"
								:style="{ width: `${displayProgressPercent}%`, background: accentColor }"></div>
						</div>
						<div class="g-block-pay-audio-playlist__time">{{ displayCurrentTime }} / {{ durationText }}
						</div>
					</div>
				</div>
				<button type="button" class="g-block-pay-audio-playlist__preview-play"
					:style="{ background: accentColor }" :aria-label="isPlaying ? '暂停' : '播放'"
					@click.stop="onPreviewPlayClick">
					<span v-if="isPlaying" class="g-block-pay-audio-playlist__pause"></span>
					<span v-else class="g-block-pay-audio-playlist__triangle"></span>
				</button>
			</div>

			<div class="g-block-pay-audio-playlist__section-head">
				<span class="g-block-pay-audio-playlist__section-title">{{ playlistData.listTitle }}</span>
				<span class="g-block-pay-audio-playlist__section-count">共 {{ totalCount }} 个</span>
			</div>

			<div class="g-block-pay-audio-playlist__scroll">
				<div v-for="entry in listEntries" @click.stop="onItemClick(entry, $event)" :key="entry.key" class="g-block-pay-audio-playlist__item" :class="{
					'is-active': entry.index === activeIndex && !entry.locked,
					'is-locked': entry.locked,
				}">
					<div class="g-block-pay-audio-playlist__item-cover">
						<img v-if="getCoverUrl(entry.item)" :src="getCoverUrl(entry.item)" alt="" />
						<i v-else class="mbm-iconfont mbm-yinpin"></i>
					</div>
					<div class="g-block-pay-audio-playlist__item-main">
						<div class="g-block-pay-audio-playlist__item-title">{{ getTrackTitle(entry.item) }}</div>
						<div class="g-block-pay-audio-playlist__item-sub">{{ artistMetaText }}</div>
					</div>
					<button v-if="!entry.locked" type="button" class="g-block-pay-audio-playlist__item-play"
						:style="{ background: accentColor }"
						:aria-label="entry.index === activeIndex && isPlaying ? '暂停' : '播放'"
						@click.stop="onItemClick(entry, $event)">
						<span v-if="entry.index === activeIndex && isPlaying"
							class="g-block-pay-audio-playlist__pause is-sm"></span>
						<span v-else class="g-block-pay-audio-playlist__triangle is-sm"></span>
					</button>
					<i v-else class="mbm-iconfont mbm-suoding g-block-pay-audio-playlist__lock"></i>
				</div>
			</div>

			<div v-if="!isUnlocked && !shouldHidePrice" class="g-block-pay-audio-playlist__footer">
				<div class="g-block-pay-audio-playlist__footer-main">
					<div class="g-block-pay-audio-playlist__price" :style="{ color: accentColor }">
						<span class="g-block-pay-audio-playlist__currency">{{ playlistData.currency }}</span>
						<span class="g-block-pay-audio-playlist__price-value">{{ playlistData.price }}</span>
					</div>
					<div class="g-block-pay-audio-playlist__pay-desc">{{ footerDesc }}</div>
				</div>
				<button type="button" class="g-block-pay-audio-playlist__unlock-btn"
					:style="{ background: accentColor }" @click.stop="onUnlockClick">{{ playlistData.buttonText
					}}</button>
			</div>
		</div>

		<audio v-if="playingUrl" ref="audioEl" :src="playingUrl" @timeupdate="onTimeUpdate"
			@loadedmetadata="onLoadedMetadata" @play="isPlaying = true" @pause="isPlaying = false"
			@ended="onPlayEnded"></audio>
	</div>
</template>

<script>
import baseFunc from '../../../../config/base_func.js';
import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
import {
	formatAudioTime,
	getAudioLibraryAudioUrl,
	getAudioLibraryCoverUrl,
	getAudioTrackTitle,
	getPayAudioPlaylistListEntries,
	isPayAudioPlaylistUnlocked,
	normalizePayAudioPlaylistData,
	setPayAudioPlaylistUnlocked,
} from '../../../utils/payAudioPlaylist.js';

export default {
	name: 'GPayAudioPlaylistShell',
	mixins: [geditPayUnlockMixin],
	props: {
		data: {
			type: Object,
			required: true,
		},
		blockId: {
			type: String,
			default: '',
		},
		editable: {
			type: Boolean,
			default: false,
		},
		active: {
			type: Boolean,
			default: false,
		},
		h5: {
			type: Boolean,
			default: false,
		},
		compact: {
			type: Boolean,
			default: false,
		},
		idKey: {
			type: String,
			default: '',
		},
	},
	emits: ['select'],
	data() {
		return {
			unlockedLocal: false,
			activeIndex: 0,
			isPlaying: false,
			playingUrl: '',
			currentTime: 0,
			duration: 0,
			permissionRequests: {},
			isDragging: false,
			draggingTime: 0,
		};
	},
	computed: {
		playlistData() {
			return normalizePayAudioPlaylistData(this.data);
		},
		listEntries() {
			return getPayAudioPlaylistListEntries(this.playlistData, {
				unlocked: this.isUnlocked,
				compact: this.compact,
			});
		},
		totalCount() {
			return this.playlistData.items.length;
		},
		isUnlocked() {
			if (this.editable) return false;
			return this.unlockedLocal || isPayAudioPlaylistUnlocked(this.blockId) || this.isGeditModulePaid;
		},
		accentColor() {
			return this.playlistData.buttonBg || '#285af9';
		},
		activeEntry() {
			return this.listEntries.find(entry => entry.index === this.activeIndex)
				|| this.listEntries[0]
				|| null;
		},
		activeItem() {
			return this.activeEntry?.item || this.playlistData.items[this.activeIndex] || null;
		},
		activeCoverUrl() {
			return this.activeItem ? getAudioLibraryCoverUrl(this.activeItem) : '';
		},
		activeTitle() {
			return this.activeItem ? getAudioTrackTitle(this.activeItem) : '';
		},
		artistMetaText() {
			const artist = this.playlistData.artist || '未知艺人';
			return `By ${artist} • ${this.totalCount} Songs`;
		},
		footerDesc() {
			const desc = this.playlistData.payDesc || '永久播放';
			return `${this.totalCount}个 • ${desc}`;
		},
		currentTimeText() {
			return formatAudioTime(this.currentTime);
		},
		durationText() {
			if (this.duration) return formatAudioTime(this.duration);
			const fallback = this.activeItem?.audio?.duration || 0;
			return formatAudioTime(fallback) || (this.activeItem?.audio?.durationText || '00:00');
		},
		progressPercent() {
			if (!this.duration) return 0;
			return Math.min(100, Math.max(0, (this.currentTime / this.duration) * 100));
		},
		displayProgressPercent() {
			if (this.isDragging) {
				if (!this.duration) return 0;
				return Math.min(100, Math.max(0, (this.draggingTime / this.duration) * 100));
			}
			return this.progressPercent;
		},
		displayCurrentTime() {
			if (this.isDragging) return formatAudioTime(this.draggingTime);
			return this.currentTimeText;
		},
	},
	watch: {
		blockId: {
			immediate: true,
			handler() {
				this.syncUnlockState();
			},
		},
		'listEntries.length'(len) {
			if (this.activeIndex >= len) {
				this.activeIndex = Math.max(0, len - 1);
				this.stopPlay();
			}
		},
	},
	beforeUnmount() {
		this.stopPlay();
		document.removeEventListener('mousemove', this._onGlobalMouseMove);
		document.removeEventListener('mouseup', this._onGlobalMouseUp);
		document.removeEventListener('touchmove', this._onGlobalTouchMove);
		document.removeEventListener('touchend', this._onGlobalTouchEnd);
	},
	methods: {
		syncUnlockState() {
			this.unlockedLocal = isPayAudioPlaylistUnlocked(this.blockId);
		},
		getCoverUrl(item) {
			return getAudioLibraryCoverUrl(item);
		},
		getTrackTitle(item) {
			return getAudioTrackTitle(item);
		},
		isEntryPlayable(entry) {
			if (!(this.editable || this.h5)) return false;
			return this.editable || !entry.locked;
		},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
		onUnlockClick() {
			this.runGeditUnlockFlow({
				applyUnlock: () => {
					setPayAudioPlaylistUnlocked(this.blockId, true);
					this.unlockedLocal = true;
				},
			});
		},
		onPreviewPlayClick() {
			if (!this.activeEntry) return;
			if (this.activeEntry.locked) {
				if (!this.editable) this.onUnlockClick();
				return;
			}
			if (!this.isEntryPlayable(this.activeEntry)) return;
			if (this.isPlaying) {
				this.pausePlay();
				return;
			}
			this.playEntry(this.activeEntry);
		},
		onItemClick(entry, event) {
			event.stopPropagation();
			if (entry.locked) {
				if (!this.editable) this.onUnlockClick();
				return;
			}
			if (!this.isEntryPlayable(entry)) {
				this.activeIndex = entry.index;
				this.currentTime = 0;
				this.duration = entry.item?.audio?.duration || 0;
				return;
			}
			if (this.activeIndex === entry.index && this.isPlaying) {
				this.pausePlay();
				return;
			}
			this.activeIndex = entry.index;
			this.playEntry(entry);
		},
		playEntry(entry) {
			const url = getAudioLibraryAudioUrl(entry.item);
			if (!url) return;
			this.currentTime = 0;
			this.duration = entry.item?.audio?.duration || 0;
			const entryKey = entry.key;

			if (this.h5 && entry.item.audio?.id && !this.permissionRequests[entryKey]) {
				this.permissionRequests[entryKey] = true;
				this.requestPlayPermission(entry, url).then(() => {
					this.$nextTick(() => {
						this.$refs.audioEl?.play?.().catch(() => { });
					});
				}).catch((err) => {
					this.permissionRequests[entryKey] = false;
					this.$message?.error?.(err.data?.message || '请求失败');
				});
				return;
			}

			this.playingUrl = url;
			this.$nextTick(() => {
				this.$refs.audioEl?.play?.().catch(() => { });
			});
		},
		getQueryStringByChannel(name) {
			const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`, 'i');
			const query = window.location.href.split('?')[1];
			if (!query) return null;
			const re = query.match(reg);
			return re != null ? unescape(re[2]) : null;
		},
		requestPlayPermission(entry, fallbackUrl) {
			return this.$apiRequest.post('/api/getMediaPlayPermissions', {
				attach_id: entry.item.audio.id,
				id_key: this.idKey,
				unique: '',
				app_type: 1,
				p_key: this.getQueryStringByChannel('p_key') || '',
			}).then((res) => {
				if (res.data?.play_url) {
					this.playingUrl = baseFunc.getQiniusign(res.data.play_url);
				} else {
					this.playingUrl = fallbackUrl;
				}
			});
		},
		pausePlay() {
			this.$refs.audioEl?.pause?.();
			this.isPlaying = false;
		},
		onTimeUpdate(event) {
			this.currentTime = event.target?.currentTime || 0;
		},
		onLoadedMetadata(event) {
			this.duration = event.target?.duration || this.duration || 0;
		},
		onProgressClick(event) {
			const track = event.currentTarget;
			if (!track) return;
			const duration = this.getSeekableDuration();
			if (!duration) return;

			const rect = track.getBoundingClientRect();
			if (!rect.width) return;
			const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
			const nextTime = ratio * duration;
			this.currentTime = nextTime;

			const el = this.$refs.audioEl;
			if (el && this.playingUrl) {
				el.currentTime = nextTime;
				return;
			}

			// 尚未开始播放时，先起播再跳转
			if (this.activeEntry && !this.activeEntry.locked && this.isEntryPlayable(this.activeEntry)) {
				this.playEntry(this.activeEntry);
				this.$nextTick(() => {
					const audio = this.$refs.audioEl;
					if (!audio) return;
					const applySeek = () => {
						audio.currentTime = nextTime;
						this.currentTime = nextTime;
						audio.removeEventListener('loadedmetadata', applySeek);
					};
					if (audio.readyState >= 1) {
						applySeek();
					} else {
						audio.addEventListener('loadedmetadata', applySeek);
					}
				});
			}
		},
		onProgressMouseDown(event) {
			this._startDrag(event.clientX, event.clientY);
			document.addEventListener('mousemove', this._onGlobalMouseMove);
			document.addEventListener('mouseup', this._onGlobalMouseUp);
		},
		onProgressTouchStart(event) {
			const touch = event.touches?.[0];
			if (!touch) return;
			this._startDrag(touch.clientX, touch.clientY);
			document.addEventListener('touchmove', this._onGlobalTouchMove, { passive: false });
			document.addEventListener('touchend', this._onGlobalTouchEnd);
		},
		_startDrag(clientX, clientY) {
			const duration = this.getSeekableDuration();
			if (!duration) return;
			this.isDragging = true;
			this.draggingTime = this.currentTime;
			const track = this.$refs.progressTrack;
			if (!track) return;
			const rect = track.getBoundingClientRect();
			if (!rect.width) return;
			const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
			this.draggingTime = ratio * duration;
		},
		_onGlobalMouseMove(event) {
			this._updateDrag(event.clientX);
		},
		_onGlobalMouseUp() {
			this._endDrag();
			document.removeEventListener('mousemove', this._onGlobalMouseMove);
			document.removeEventListener('mouseup', this._onGlobalMouseUp);
		},
		_onGlobalTouchMove(event) {
			event.preventDefault();
			const touch = event.touches?.[0];
			if (!touch) return;
			this._updateDrag(touch.clientX);
		},
		_onGlobalTouchEnd() {
			this._endDrag();
			document.removeEventListener('touchmove', this._onGlobalTouchMove);
			document.removeEventListener('touchend', this._onGlobalTouchEnd);
		},
		_updateDrag(clientX) {
			if (!this.isDragging) return;
			const duration = this.getSeekableDuration();
			if (!duration) return;
			const track = this.$refs.progressTrack;
			if (!track) return;
			const rect = track.getBoundingClientRect();
			if (!rect.width) return;
			const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
			this.draggingTime = ratio * duration;
		},
		_endDrag() {
			if (!this.isDragging) return;
			const nextTime = this.draggingTime;
			this.isDragging = false;
			this.currentTime = nextTime;
			const el = this.$refs.audioEl;
			if (el && this.playingUrl) {
				el.currentTime = nextTime;
			}
		},
		getSeekableDuration() {
			const el = this.$refs.audioEl;
			if (el?.duration && Number.isFinite(el.duration)) {
				return el.duration;
			}
			if (this.duration && Number.isFinite(this.duration)) {
				return this.duration;
			}
			return this.activeItem?.audio?.duration || 0;
		},
		onPlayEnded() {
			this.isPlaying = false;
			this.currentTime = 0;
		},
		stopPlay() {
			const el = this.$refs.audioEl;
			if (el) el.pause();
			this.playingUrl = '';
			this.isPlaying = false;
			this.currentTime = 0;
		},
	},
};
</script>

<style scoped lang="less">
.g-block-pay-audio-playlist {
	box-sizing: border-box;

	&--edit {
		border: 1px solid transparent;
		border-radius: 12px;
		transition: border-color 0.2s, box-shadow 0.2s;
		cursor: pointer;

		&.is-active {
			border-color: #285af9;
			box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
		}
	}
}

.g-block-pay-audio-playlist__card {
	overflow: hidden;
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
	padding: 14px 12px 12px;
}

.g-block-pay-audio-playlist__preview {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px;
	border-radius: 12px;
	background: #eef3ff;
}

.g-block-pay-audio-playlist__preview-cover {
	flex-shrink: 0;
	width: 90px;
	height: 90px;
	border-radius: 10px;
	overflow: hidden;
	background: #dfe7f8;
	display: flex;
	align-items: center;
	justify-content: center;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	i {
		font-size: 28px;
		color: #9db0d8;
	}
}

.g-block-pay-audio-playlist__preview-main {
	flex: 1;
	min-width: 0;
}

.g-block-pay-audio-playlist__listening {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	color: #285af9;
	line-height: 1.2;
	margin-top: 8px;
}

.g-block-pay-audio-playlist__dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #285af9;
}

.g-block-pay-audio-playlist__preview-title {
	margin-top: 8px;
	font-size: 16px;
	font-weight: 700;
	color: #1a1a1a;
	line-height: 1.3;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.g-block-pay-audio-playlist__preview-sub {
	margin-top: 8px;
	font-size: 12px;
	color: #8a8f99;
	line-height: 1.3;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.g-block-pay-audio-playlist__progress {
	margin-top: 8px;
}

.g-block-pay-audio-playlist__progress-track {
	position: relative;
	height: 4px;
	border-radius: 999px;
	background: #d7e0f5;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		top: -8px;
		bottom: -8px;
	}

	&.is-interactive {
		cursor: pointer;
	}
}

.g-block-pay-audio-playlist__progress-fill {
	height: 100%;
	border-radius: inherit;
	width: 0;
	pointer-events: none;
	transition: width 0.15s linear;
}

.g-block-pay-audio-playlist__time {
	margin-top: 4px;
	font-size: 11px;
	color: #8a8f99;
	line-height: 1.2;
}

.g-block-pay-audio-playlist__preview-play {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border: none;
	border-radius: 50%;
	cursor: pointer;
	padding: 0;
	margin-top: 60px;
}

.g-block-pay-audio-playlist__triangle {
	width: 0;
	height: 0;
	margin-left: 3px;
	border-style: solid;
	border-width: 7px 0 7px 12px;
	border-color: transparent transparent transparent #fff;

	&.is-sm {
		border-width: 5px 0 5px 8px;
		margin-left: 2px;
	}
}

.g-block-pay-audio-playlist__pause {
	position: relative;
	width: 12px;
	height: 14px;

	&::before,
	&::after {
		content: '';
		position: absolute;
		top: 0;
		width: 3px;
		height: 100%;
		border-radius: 1px;
		background: #fff;
	}

	&::before {
		left: 0;
	}

	&::after {
		right: 0;
	}

	&.is-sm {
		width: 10px;
		height: 12px;

		&::before,
		&::after {
			width: 2px;
		}
	}
}

.g-block-pay-audio-playlist__section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 14px;
	margin-bottom: 10px;
}

.g-block-pay-audio-playlist__section-title {
	font-size: 15px;
	font-weight: 700;
	color: #1a1a1a;
}

.g-block-pay-audio-playlist__section-count {
	font-size: 12px;
	color: #999;
}

.g-block-pay-audio-playlist__scroll {
	/* 5 首可见：item 56px + gap 10px * 4 */
	max-height: calc(5 * 56px + 4 * 10px);
	overflow-x: hidden;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 10px;
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
		width: 0;
		height: 0;
	}
}

.g-block-pay-audio-playlist__item {
	display: flex;
	align-items: center;
	gap: 10px;
	min-height: 56px;
	box-sizing: border-box;
	cursor: pointer;
}

.g-block-pay-audio-playlist__item-cover {
	flex-shrink: 0;
	width: 48px;
	height: 48px;
	border-radius: 8px;
	overflow: hidden;
	background: #f0f0f0;
	display: flex;
	align-items: center;
	justify-content: center;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	i {
		font-size: 20px;
		color: #bfbfbf;
	}
}

.g-block-pay-audio-playlist__item-main {
	flex: 1;
	min-width: 0;
}

.g-block-pay-audio-playlist__item-title {
	font-size: 14px;
	font-weight: 600;
	color: #1a1a1a;
	line-height: 1.3;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.g-block-pay-audio-playlist__item-sub {
	margin-top: 4px;
	font-size: 12px;
	color: #999;
	line-height: 1.3;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.g-block-pay-audio-playlist__item-play {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border: none;
	border-radius: 50%;
	cursor: pointer;
	padding: 0;
}

.g-block-pay-audio-playlist__lock {
	flex-shrink: 0;
	font-size: 18px;
	color: #666;
}

.g-block-pay-audio-playlist__footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 14px;
	padding-top: 12px;
	border-top: 1px solid #f0f2f5;
}

.g-block-pay-audio-playlist__footer-main {
	min-width: 0;
	flex: 1;
}

.g-block-pay-audio-playlist__price {
	display: inline-flex;
	align-items: baseline;
	line-height: 1;
}

.g-block-pay-audio-playlist__currency {
	font-size: 14px;
	font-weight: 600;
	margin-right: 1px;
}

.g-block-pay-audio-playlist__price-value {
	font-size: 28px;
	font-weight: 700;
}

.g-block-pay-audio-playlist__pay-desc {
	margin-top: 4px;
	font-size: 12px;
	color: #999;
	line-height: 1.3;
}

.g-block-pay-audio-playlist__unlock-btn {
	flex-shrink: 0;
	border: none;
	border-radius: 999px;
	padding: 12px 20px;
	font-size: 14px;
	font-weight: 600;
	color: #fff;
	cursor: pointer;
	white-space: nowrap;
	line-height: 1;
}

.g-block-pay-audio-playlist--compact {
	.g-block-pay-audio-playlist__scroll {
		max-height: calc(3 * 56px + 2 * 10px);
	}
}

.g-block-pay-audio-playlist--h5 {
	.g-block-pay-audio-playlist__card {
		border-radius: 0.24rem;
		padding: 0.28rem 0.24rem 0.24rem;
		box-shadow: 0 0 0.24rem rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-audio-playlist__preview {
		gap: 0.24rem;
		padding: 0.24rem;
		border-radius: 0.24rem;
	}

	.g-block-pay-audio-playlist__preview-cover {
		width: 1.8rem;
		height: 1.8rem;
		border-radius: 0.2rem;

		i {
			font-size: 0.56rem;
		}
	}

	.g-block-pay-audio-playlist__listening {
		gap: 0.12rem;
		font-size: 0.24rem;
		margin-top: 0.16rem;
	}

	.g-block-pay-audio-playlist__dot {
		width: 0.12rem;
		height: 0.12rem;
	}

	.g-block-pay-audio-playlist__preview-title {
		margin-top: 0.16rem;
		font-size: 0.32rem;
	}

	.g-block-pay-audio-playlist__preview-sub {
		font-size: 0.24rem;
		margin-top: 0.16rem;
	}

	.g-block-pay-audio-playlist__progress {
		margin-top: 0.16rem;
	}

	.g-block-pay-audio-playlist__progress-track {
		height: 0.08rem;
	}

	.g-block-pay-audio-playlist__time {
		margin-top: 0.08rem;
		font-size: 0.22rem;
	}

	.g-block-pay-audio-playlist__preview-play {
		width: 0.88rem;
		height: 0.88rem;
		margin-top: 1.2rem;
	}

	.g-block-pay-audio-playlist__triangle {
		border-width: 0.14rem 0 0.14rem 0.24rem;
		margin-left: 0.06rem;

		&.is-sm {
			border-width: 0.1rem 0 0.1rem 0.16rem;
			margin-left: 0.04rem;
		}
	}

	.g-block-pay-audio-playlist__section-head {
		margin-top: 0.28rem;
		margin-bottom: 0.2rem;
	}

	.g-block-pay-audio-playlist__section-title {
		font-size: 0.3rem;
	}

	.g-block-pay-audio-playlist__section-count {
		font-size: 0.24rem;
	}

	.g-block-pay-audio-playlist__scroll {
		max-height: calc(5 * 1.12rem + 4 * 0.2rem);
		gap: 0.2rem;
	}

	.g-block-pay-audio-playlist__item {
		gap: 0.2rem;
		min-height: 1.12rem;
	}

	.g-block-pay-audio-playlist__item-cover {
		width: 0.96rem;
		height: 0.96rem;
		border-radius: 0.16rem;

		i {
			font-size: 0.4rem;
		}
	}

	.g-block-pay-audio-playlist__item-title {
		font-size: 0.28rem;
	}

	.g-block-pay-audio-playlist__item-sub {
		margin-top: 0.08rem;
		font-size: 0.24rem;
	}

	.g-block-pay-audio-playlist__item-play {
		width: 0.56rem;
		height: 0.56rem;
	}

	.g-block-pay-audio-playlist__lock {
		font-size: 0.36rem;
	}

	.g-block-pay-audio-playlist__footer {
		gap: 0.24rem;
		margin-top: 0.28rem;
		padding-top: 0.24rem;
	}

	.g-block-pay-audio-playlist__currency {
		font-size: 0.28rem;
	}

	.g-block-pay-audio-playlist__price-value {
		font-size: 0.56rem;
	}

	.g-block-pay-audio-playlist__pay-desc {
		margin-top: 0.08rem;
		font-size: 0.24rem;
	}

	.g-block-pay-audio-playlist__unlock-btn {
		padding: 0.24rem 0.4rem;
		font-size: 0.28rem;
	}
}
</style>
